import { http, PaginatedResult, type RequestOptions } from '@/chassis/data/services/http-client';

export type Id = string | number;

export type BaseCrudPaths = {
    basePath: string | readonly string[]; // e.g. '/publictenders'
};

export type Normalizers<TRecord> = {
    normalizeRecord?: (raw: any) => TRecord;
    mapPaginated?: (raw: any) => PaginatedResult<TRecord>;
};

export type ServiceOptions<TRecord> = BaseCrudPaths & Normalizers<TRecord>;

/**
 * Contrato de IDs do frontend:
 * - IDs principais (path params de recurso, ex.: GET /publictenders/:id) NUNCA devem ir prefixados.
 * - IDs secundarios/FKs no payload e filtros (ex.: tenderId, supplierId, publicTenderId) DEVEM preservar prefixo.
 *
 * Esta normalizacao existe somente para operacoes por ID principal do BaseService.
 */
export function normalizePrimaryResourceId(id: Id): Id {
    if (typeof id !== 'string') {
        return id;
    }

    const trimmed = id.trim();
    if (!trimmed) {
        return trimmed;
    }

    const match = /^([A-Za-z][A-Za-z0-9_]*):(.*)$/.exec(trimmed);
    if (!match) {
        return trimmed;
    }

    const unprefixed = match[2].trim();
    if (!unprefixed) {
        return trimmed;
    }

    if ((import.meta as any)?.env?.DEV) {
        console.warn('[BaseService] Primary resource ID received with prefix; stripping before API call.', {
            originalId: trimmed,
            normalizedId: unprefixed,
        });
    }

    return unprefixed;
}

function identity<T>(v: T): T {
    return v;
}

function defaultNormalizeRecord<T>(raw: any): T {
    return raw as T;
}

function defaultMapPaginated<T>(raw: any, normalize: (r: any) => T): PaginatedResult<T> {
    const itemsSource = Array.isArray(raw?.items)
        ? raw.items
        : Array.isArray(raw?.data)
        ? raw.data
        : Array.isArray(raw)
        ? raw
        : [];
    const itemsRaw = itemsSource;
    const items = itemsRaw.map(normalize);
    return {
        items,
        pagination: raw?.pagination ?? raw?.meta?.pagination,
        summary: raw?.summary,
        appliedFilters: raw?.appliedFilters ?? raw?.applied_filters,
    };
}

export class BaseService<TRecord = any, TCreateDto = any, TUpdateDto = Partial<TCreateDto>, TFilters = Record<string, any>> {
    protected readonly basePath: string;
    protected readonly basePaths: string[];
    protected readonly normalizeRecord: (raw: any) => TRecord;
    protected readonly mapPaginatedImpl: (raw: any) => PaginatedResult<TRecord>;

    constructor(options: ServiceOptions<TRecord>) {
        this.basePaths = Array.isArray(options.basePath) ? [...options.basePath] : [options.basePath];
        this.basePath = this.basePaths[0];
        this.normalizeRecord = options.normalizeRecord || defaultNormalizeRecord;
        this.mapPaginatedImpl = options.mapPaginated || ((raw) => defaultMapPaginated<TRecord>(raw, this.normalizeRecord));
    }

    async list(filters: TFilters = {} as TFilters): Promise<PaginatedResult<TRecord>> {
        return this.withFallback(async (path) => {
            const raw = await http.get(path, filters as any, this.getRequestOptions(path));
            return this.mapPaginatedImpl(raw);
        });
    }

    async get(id: Id): Promise<TRecord> {
        return this.withFallback(async (path) => {
            const primaryId = normalizePrimaryResourceId(id);
            const raw = await http.get(`${path}/${primaryId}`, undefined, this.getRequestOptions(path));
            return this.normalizeRecord(raw);
        });
    }

    async create(dto: TCreateDto): Promise<TRecord> {
        const payload = attachUserIdIfMissing(dto);
        return this.withFallback(async (path) => {
            const raw = await http.post(path, payload as any, this.getRequestOptions(path));
            return this.normalizeRecord(raw);
        });
    }

    async update(id: Id, dto: TUpdateDto): Promise<TRecord> {
        const payload = attachUserIdIfMissing(dto);
        return this.withFallback(async (path) => {
            const primaryId = normalizePrimaryResourceId(id);
            const raw = await http.put(`${path}/${primaryId}`, payload as any, this.getRequestOptions(path));
            return this.normalizeRecord(raw);
        });
    }

    async delete(id: Id): Promise<void> {
        await this.withFallback(async (path) => {
            const primaryId = normalizePrimaryResourceId(id);
            await http.delete(`${path}/${primaryId}`, this.getRequestOptions(path));
        });
    }

    /**
     * Toggle active status using only the canonical `active` flag.
     */
    async toggleActive(id: Id, active: boolean): Promise<TRecord> {
        return this.update(id, { active } as TUpdateDto);
    }

    /**
     * Expose the current service configuration so helper utilities (e.g., BaseListService)
     * can reuse the same HTTP wiring without duplicating options.
     */
    public getServiceOptions(): ServiceOptions<TRecord> {
        return {
            basePath: this.basePaths.length === 1 ? this.basePath : [...this.basePaths],
            normalizeRecord: this.normalizeRecord,
            mapPaginated: this.mapPaginatedImpl,
        };
    }

    private async withFallback<T>(operation: (path: string) => Promise<T>): Promise<T> {
        let lastError: any;
        for (let index = 0; index < this.basePaths.length; index += 1) {
            const path = this.basePaths[index];
            try {
                return await operation(path);
            } catch (error: any) {
                const shouldTryFallback = this.shouldFallback(error, index);
                if (!shouldTryFallback) throw error;
                lastError = error;
            }
        }
        throw lastError;
    }

    protected shouldFallback(error: any, pathIndex: number): boolean {
        if (pathIndex >= this.basePaths.length - 1) return false;
        const status = error?.status ?? error?.response?.status ?? error?.cause?.status;
        return status === 404;
    }

    protected getRequestOptions(path: string): Omit<RequestOptions, 'method' | 'query' | 'body'> | undefined {
        return LEGACY_PREFIX_REGEX.test(path) ? { allowLegacyPath: true } : undefined;
    }
}

const LEGACY_PREFIX_REGEX = /^\/(syd|opd|agd|odd|igd|add)-/i;

const AUTH_SNAPSHOT_STORAGE_KEY = 'cdcb.auth.snapshot';

function attachUserIdIfMissing<T>(dto: T): T {
    if (!dto || typeof dto !== 'object') {
        return dto;
    }

    const record = dto as Record<string, unknown>;
    const existing = record.userId;
    if (typeof existing === 'string' ? existing.trim().length > 0 : existing !== undefined && existing !== null) {
        return dto;
    }

    const currentUserId = resolveStoredUserId();
    if (!currentUserId) {
        return dto;
    }

    return { ...record, userId: currentUserId } as T;
}

function resolveStoredUserId(): string | null {
    if (typeof window === 'undefined' || !window?.localStorage) {
        return null;
    }

    try {
        const raw = window.localStorage.getItem(AUTH_SNAPSHOT_STORAGE_KEY);
        if (!raw) {
            return null;
        }

        const snapshot = JSON.parse(raw) as { user?: { id?: unknown }; userId?: unknown } | null;
        const candidate = snapshot?.user?.id ?? snapshot?.userId;
        return typeof candidate === 'string' && candidate ? candidate : null;
    } catch (error) {
        return null;
    }
}
